import { useState, useMemo } from 'react';
import * as AppPics from 'assets/images';
import { notifier } from 'components/Toaster';
import TablePaginate from 'components/Paginate';
import usePaginateDefaults from 'hooks/usePaginateDefaults';
import useDebounce from 'hooks/useDebounce';
import useGetAdminRequests from 'hooks/API/GET/useGetAdminRequests';
import { useUpdateRequestStatus, useDeleteUserRequest } from 'hooks/API/POST/useManageUserRequests';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';
import StatusSelect from './StatusSelect';

function RequestsTab() {
  const [params, setParams] = usePaginateDefaults();
  const [searchTerm, setSearchTerm] = useState('');
  const [enableFilter, setEnableFilter] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  const debouncedSearch = useDebounce(searchTerm, 600);

  const queryParams = useMemo(
    () => ({
      ...params,
      keyword: debouncedSearch || undefined,
      type: filterType || undefined,
      status: filterStatus || undefined,
    }),
    [params, debouncedSearch, filterType, filterStatus]
  );

  const { data, isLoading } = useGetAdminRequests({
    enabled: true,
    config: { params: queryParams },
    queryKey: ['admin-user-requests', queryParams],
  });

  const requests = data?.data?.result ?? [];
  const { page = 1, total = 0 } = data?.data?.meta ?? {};

  const { mutate: updateStatus } = useUpdateRequestStatus();
  const { mutate: deleteRequest } = useDeleteUserRequest();

  const handleStatusChange = (requestId, status) => {
    updateStatus(
      { requestId, status },
      {
        onSuccess: () => notifier.success('Status updated'),
        onError: () => notifier.error('Failed to update status'),
      }
    );
  };

  const handleDeleteConfirm = () => {
    deleteRequest(deleteModal.id, {
      onSuccess: () => notifier.success('Request removed'),
      onError: () => notifier.error('Delete failed'),
    });
    setDeleteModal({ open: false, id: null });
  };

  const clearFilters = () => {
    setFilterType('');
    setFilterStatus('');
    setSearchTerm('');
    setParams((prev) => ({ ...prev, page: 0 }));
    setEnableFilter(false);
  };

  const hasActiveFilters = filterType || filterStatus;

  return (
    <>
      <BasicDelete
        open={deleteModal.open}
        closeModal={() => setDeleteModal({ open: false, id: null })}
        Header="Remove Request"
        Body="Are you sure you want to remove this request? This cannot be undone."
        handleConfirmation={handleDeleteConfirm}
      />

      {/* ── Search + Filter header ── */}
      <div className="tab__contentMenu mb-3">
        <ul className="tab__contentMenu-list">
          <li>
            <h2 className="prime-head p-0 boldtitle text-nowrap" style={{ fontSize: 16 }}>
              User Requests
            </h2>
          </li>
        </ul>

        <div className="mainHeader__search">
          <ul className="tab__contentMenu-list">
            <li className="relative">
              <input
                className="mainHeader__searchInput"
                placeholder="Search by title, description…"
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setParams((prev) => ({ ...prev, page: 0 }));
                }}
              />
              <span className="mainHeader__searchBtn">
                <img src={AppPics.TopMenuSearchSvg} alt="Search" />
              </span>
            </li>
            <li className="px-4 flex items-center">
              <button
                type="button"
                onClick={() => setEnableFilter(!enableFilter)}
                className="flex items-center gap-1"
              >
                <img src={AppPics.TabFilterSvg} alt="Filter" />
                <span className="viewsettingtitle">Filter</span>
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* ── Filter panel ── */}
      {enableFilter && (
        <div className="row mt-2 mb-3">
          <div className="col-lg-3">
            <StatusSelect
              options={[
                { value: 'topic', label: 'Topic' },
                { value: 'feature', label: 'Feature' },
              ]}
              value={filterType}
              onChange={(v) => {
                setFilterType(v);
                setParams((p) => ({ ...p, page: 0 }));
              }}
              label="Request Type"
              isClearable
              placeholder="All types"
            />
          </div>

          <div className="col-lg-4">
            <StatusSelect
              value={filterStatus}
              onChange={(v) => {
                setFilterStatus(v);
                setParams((p) => ({ ...p, page: 0 }));
              }}
              isClearable
              placeholder="All statuses"
            />
          </div>

          <div className="col-lg-3 d-flex" style={{ alignItems: 'flex-end' }}>
            <button type="button" className="btn btn-plane p--tint" onClick={clearFilters}>
              Clear filter
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="tab__contentBody">
        <div className="tableWrap">
          <div className="table-responsive">
            <table className="table tableWrap__table">
              <thead className="thead-light __appTheadColor">
                <tr>
                  <th className="text-center">#</th>
                  <th>User</th>
                  <th className="text-center">Type</th>
                  <th>Title &amp; Description</th>
                  <th>Category / Priority</th>
                  <th className="text-center">Date</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      Loading…
                    </td>
                  </tr>
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      No requests found.
                    </td>
                  </tr>
                ) : (
                  requests.map((req, idx) => (
                    <tr key={req._id}>
                      <td className="text-center">{(page - 1) * params.limit + idx + 1}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{req.userId?.name ?? '—'}</div>
                        <small className="text-muted">
                          {req.userId?.email ?? req.userId?.mobilFull ?? ''}
                        </small>
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge ${
                            req.type === 'topic' ? 'badge-primary' : 'badge-warning'
                          }`}
                        >
                          {req.type}
                        </span>
                      </td>
                      <td style={{ maxWidth: 260 }}>
                        <div style={{ fontWeight: 500 }}>{req.title}</div>
                        <small className="text-muted">{req.description}</small>
                      </td>
                      <td>{req.category ?? req.priority ?? '—'}</td>
                      <td className="text-center" style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                        {new Date(req.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        <StatusSelect
                          value={req.status}
                          onChange={(v) => v && handleStatusChange(req._id, v)}
                          showLabel={false}
                          isCompact
                        />
                      </td>
                      <td className="text-center">
                        <button
                          className="actions-icons text-danger"
                          title="Delete"
                          onClick={() => setDeleteModal({ open: true, id: req._id })}
                        >
                          <img src={AppPics.DeleteSvg} alt="Delete" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <TablePaginate
          currentPage={page}
          maxCount={total}
          defaultLimit={params.limit}
          setPage={(p) => setParams((prev) => ({ ...prev, page: p }))}
          handleChange={(limit) => setParams((prev) => ({ ...prev, limit, page: 0 }))}
        />
      </div>
    </>
  );
}

export default RequestsTab;
